import { Cesium } from 'app/cesium';
import { TerrainService } from 'app/map/services/terrain.service';
import { distancesBetween, minDistanceBetween } from './helpers';

export interface Cartographic {
    latitude: number; // Meters
    longitude: number; // Meters
    height: number; // Meters
    degrees?: boolean;
}

export async function fetchTerrainHeights(
    cartographicSamples: Cesium.Cartographic[],
    terrainProvider: Cesium.TerrainProvider,
    terrainService: TerrainService,
    mostDetailed = false,
): Promise<Cesium.Cartographic[]> {
    const TERRAIN_DETAIL_LEVEL = terrainService.findImsTerrainProviderDetailLevel();

    if (!terrainProvider.availability) {
        return Promise.reject<Cesium.Cartographic[]>(
            'Terrain provider data is not currently available.',
        );
    }
    let cesiumPromise: Promise<Cartographic[]>;

    if (mostDetailed) {
        cesiumPromise = Cesium.sampleTerrainMostDetailed(terrainProvider, cartographicSamples);
    } else {
        cesiumPromise = Cesium.sampleTerrain(
            terrainProvider,
            TERRAIN_DETAIL_LEVEL,
            cartographicSamples,
        );
    }

    return new Promise<Cesium.Cartographic[]>((resolve, reject) => {
        cesiumPromise.catch((err: any) => {
            reject(err);
        });
        Promise.resolve(cesiumPromise)
            .then(() => {
                // Cartograph heights may be undefined signalling that the fetch for that sample failed.
                // Choosing not to handle here and handling where path profiles are built  but consumers of this function
                // will need to be aware if this is ever used by other things
                resolve(cartographicSamples);
            })
            .catch((err) => reject(err));
    });
}

export function generateTerrainSamples(
    startPoint: Cesium.Cartographic,
    endPoint: Cesium.Cartographic,
): {
    terrainSamples: Cesium.Cartographic[];
    totalDistance: number;
    distanceBetween: number;
    distances: number[];
} {
    const MAX_POINTS = 1000; // Cap for performance reasons
    const MIN_POINTS = 250; // Cap for chart accuracy

    const IDEAL_DISTANCE_BETWEEN = 10; // metres

    // Split up the total distance into samples
    const geodesic = new Cesium.EllipsoidGeodesic(startPoint, endPoint);
    const totalDistance = geodesic.surfaceDistance;

    if (isNaN(totalDistance)) {
        throw 'Unable to generate terrain samples for this distance';
    }

    const distanceBetween = minDistanceBetween(
        totalDistance,
        MAX_POINTS,
        MIN_POINTS,
        IDEAL_DISTANCE_BETWEEN,
    );

    const distances = distancesBetween(totalDistance, distanceBetween);

    // We do not copy the start or end point provided as we want to mutate their height value.
    const terrainSamples = distances.map((distance) => {
        return geodesic.interpolateUsingSurfaceDistance(distance) as Cesium.Cartographic;
    });

    // Add our start and end point
    terrainSamples.unshift(startPoint);
    distances.unshift(0);
    terrainSamples.push(endPoint);
    distances.push(totalDistance);

    return { terrainSamples, totalDistance, distanceBetween, distances };
}

// hack to get unit tests to work
export class pathProfileFunctionsWrapper {
    static generateTerrainSamples = generateTerrainSamples;
    static fetchTerrainHeights = fetchTerrainHeights;
}
